'use client';

import { useTranslations } from 'next-intl';
import { ComponentProps, ReactNode, createContext, useContext } from 'react';
import { Button } from '@/shared/ui/button';
import { Layout } from '@/shared/ui/layout';
import { Modal } from '@/shared/ui/modal';
import { ModalProps } from '@/shared/ui/modal/types';
import './order-autofill-modal.scss';

type Props = {
    onConfirm?(): void;
    onAbort?(): void;
    children?: ReactNode;
} & ModalProps;

type OrderAutoFillModalContextValue = {
    onClose: ModalProps['onClose'];
    onConfirm?: () => void;
    onAbort?: () => void;
};

const OrderAutoFillModalContext = createContext<OrderAutoFillModalContextValue | null>(null);

const useOrderAutoFillModalContext = () => {
    const context = useContext(OrderAutoFillModalContext);
    if (!context) {
        throw new Error('OrderAutoFillModal components must be used within OrderAutoFillModalWrapper.');
    }
    return context;
};

export const OrderAutoFillModalWrapper = ({ onConfirm, onAbort, children, ...props }: Props) => {
    return (
        <OrderAutoFillModalContext.Provider value={{ onClose: props.onClose, onConfirm, onAbort }}>
            <Modal
                {...props}
                wrapperProps={{
                    className: 'order-autofill-modal',
                    'data-testid': 'order-autofill-modal',
                }}
            >
                {children}
            </Modal>
        </OrderAutoFillModalContext.Provider>
    );
};

/** Provider that only provides context and renders children (Modal prefab). */
export const OrderAutoFillModalProvider = ({ onConfirm, onAbort, children, ...props }: Props) => {
    return (
        <OrderAutoFillModalContext.Provider value={{ onClose: props.onClose, onConfirm, onAbort }}>
            {children}
        </OrderAutoFillModalContext.Provider>
    );
};

/** Renders only the title text. Use inside prefab: "Modal.Title". */
const OrderAutoFillModalTitle = () => {
    const t = useTranslations();
    return <>{t('ORDER_AUTOFILL_MODAL.TITLE')}</>;
};

/** Renders only the description text. Use inside prefab: "Modal.Description". */
const OrderAutoFillModalDescription = () => {
    const t = useTranslations();
    return <>{t('ORDER_AUTOFILL_MODAL.DESCRIPTION')}</>;
};

type ActionButtonProps = ComponentProps<typeof Button>;

const OrderAutoFillModalNoButton = ({ onClick, ...props }: ActionButtonProps) => {
    const t = useTranslations();
    const { onClose, onAbort } = useOrderAutoFillModalContext();

    return (
        <Layout
            element={Button}
            variant="secondary"
            data-testid="order-autofill-modal-no-button"
            onClick={(event) => {
                onClick?.(event);
                onClose();
                onAbort?.();
            }}
            {...props}
        >
            {t('WORDS.NO')}
        </Layout>
    );
};

const OrderAutoFillModalYesButton = ({ onClick, ...props }: ActionButtonProps) => {
    const t = useTranslations();
    const { onClose, onConfirm } = useOrderAutoFillModalContext();

    return (
        <Layout
            element={Button}
            data-testid="order-autofill-modal-yes-button"
            onClick={(event) => {
                onClick?.(event);
                onClose();
                onConfirm?.();
            }}
            {...props}
        >
            {t('WORDS.YES')}
        </Layout>
    );
};

export { OrderAutoFillModalWrapper as OrderAutoFillModal };

export {
    OrderAutoFillModalWrapper as Wrapper,
    OrderAutoFillModalProvider as Provider,
    OrderAutoFillModalTitle as Title,
    OrderAutoFillModalDescription as Description,
    OrderAutoFillModalNoButton as NoButton,
    OrderAutoFillModalYesButton as YesButton,
};
